
import {
    TenementQueryTool
} from "../tenements/TenementQueryTool "
import { Form } from "react-bootstrap"
import Button from 'react-bootstrap/Button';
import Collapse from 'react-bootstrap/Collapse';
import { Fragment, useState, useMemo } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMap } from "@vis.gl/react-maplibre";
import { AssayQueryToolMapLibre } from "../soil-assay/AssayQueryTool";
import { MinedexQueryTool } from "../minedex/MinedexQueryTool";
import { faFilter, faMap, faLayerGroup, faQuestionCircle, faChevronDown, faChevronUp, faGem, faSeedling, faMountain, faUserShield } from '@fortawesome/free-solid-svg-icons'
import { DrillHoleQueryTool } from "../drill-hole/DrillHoleQueryTool";
import { useIsAdmin } from "../auth/useIsAdmin";
import { ASTER_LAYERS, asterLegendUrl, slipGeologyLegendUrl, SHOW_BEDROCK_LEGEND,
         SHOW_BAREST_EARTH, SHOW_INTERPRETED_BEDROCK, SHOW_LAND_CHANGE, SHOW_VEG_WATER,
         VEG_WATER_DEA_IDS } from '../gl-map-optimized';
import { DEA_OVERLAYS } from '../imagery/sentinelSource';
import { GoldFinderLogo } from '../branding/GoldFinderLogo';
import { OpacitySlider, FilterSection, Chip, ChipRow } from '../filters/FilterControls';
import { ProspectFilter } from '../prospect/ProspectFilter';
import { PlatesSidebarCards } from '../plates/PlatesSidebarCards';
import { OCR_GOLD_AVAILABLE, OCR_GOLD_LOCAL, OCR_GOLD_CONFIDENCE, OCR_GOLD_LEGEND, OCR_GOLD_VECTOR_MINZOOM } from '../ocr-gold/ocrGoldStyle';
import {
    ALLUVIAL_SIGNAL_AVAILABLE, ALLUVIAL_SIGNAL_LOCAL, ALLUVIAL_SIGNAL_CLASSES, ALLUVIAL_RECENCY,
    ALLUVIAL_RECENCY_BANDS, ALLUVIAL_SIGNAL_ERROR_MINZOOM, alluvialRecency, alluvialRecencyPatch,
} from '../asx-alluvial/asxAlluvialStyle';

// Sidebar CSS styles
const styles = {
    sidebar: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        fontFamily: 'var(--gf-font-sans, "Poppins", sans-serif)',
    },
    header: {
        backgroundColor: 'white',
        padding: 'var(--gf-space-4, 16px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: '1px solid var(--gf-border-color, #E5E7EB)',
    },
    headerIcon: {
        fontSize: 'var(--gf-text-xl, 1.25rem)',
    },
    headerTitle: {
        fontSize: 'var(--gf-text-lg, 1.125rem)',
        fontWeight: 'var(--gf-font-semibold, 600)',
        margin: 0,
    },
    content: {
        flex: 1,
        overflowY: 'auto',
        padding: 'var(--gf-space-3, 12px)',
    },
    section: {
        marginBottom: 'var(--gf-space-4, 16px)',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gf-space-2, 8px)',
        paddingBottom: 'var(--gf-space-2, 8px)',
        borderBottom: '2px solid var(--gf-primary-400, #FD7218)',
        marginBottom: 'var(--gf-space-3, 12px)',
    },
    sectionIcon: {
        color: 'var(--gf-primary-400, #FD7218)',
        fontSize: 'var(--gf-text-base, 1rem)',
    },
    sectionTitle: {
        fontSize: 'var(--gf-text-sm, 0.875rem)',
        fontWeight: 'var(--gf-font-semibold, 600)',
        color: 'var(--gf-secondary-700, #2D3238)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        margin: 0,
    },
    baseGroup: {
        marginBottom: 'var(--gf-space-2, 8px)',
    },
    baseGroupHeading: {
        fontSize: 'var(--gf-text-xs, 0.75rem)',
        fontWeight: 'var(--gf-font-semibold, 600)',
        color: 'var(--gf-secondary-500, #4B5563)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '0 var(--gf-space-2, 8px)',
        marginBottom: 'var(--gf-space-1, 4px)',
    },
    // Two-up keeps the picker compact; a `wide` layer spans both columns when
    // its label needs the room.
    baseGroupGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        columnGap: 'var(--gf-space-1, 4px)',
        rowGap: '2px',
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--gf-space-1, 4px)',
    },
    radioItem: {
        display: 'flex',
        alignItems: 'center',
        padding: 'var(--gf-space-2, 8px) var(--gf-space-3, 12px)',
        borderRadius: 'var(--gf-border-radius-md, 6px)',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        fontSize: 'var(--gf-text-sm, 0.875rem)',
    },
    overlayCard: {
        border: '1px solid var(--gf-border-color, #E5E7EB)',
        borderRadius: 'var(--gf-border-radius-lg, 8px)',
        marginBottom: 'var(--gf-space-2, 8px)',
        overflow: 'hidden',
        backgroundColor: 'white',
    },
    overlayHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--gf-space-3, 12px)',
        backgroundColor: 'var(--gf-secondary-50, #F5F6F7)',
    },
    overlayCheckbox: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gf-space-2, 8px)',
        flex: 1,
    },
    overlayControls: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gf-space-2, 8px)',
    },
    filterButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        padding: 0,
        border: '1px solid var(--gf-border-color, #E5E7EB)',
        borderRadius: 'var(--gf-border-radius-md, 6px)',
        backgroundColor: 'white',
        color: 'var(--gf-secondary-500, #4B5563)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
    filterButtonActive: {
        backgroundColor: 'var(--gf-primary-400, #FD7218)',
        borderColor: 'var(--gf-primary-400, #FD7218)',
        color: 'white',
    },
    filterContent: {
        borderTop: '1px solid var(--gf-border-color, #E5E7EB)',
        padding: 'var(--gf-space-3, 12px)',
        fontSize: 'var(--gf-text-xs, 0.75rem)',
        backgroundColor: 'white',
    },
    inputGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gf-space-2, 8px)',
    },
    inputLabel: {
        fontSize: 'var(--gf-text-xs, 0.75rem)',
        color: 'var(--gf-secondary-500, #4B5563)',
        whiteSpace: 'nowrap',
    },
    numberInput: {
        width: '70px',
        padding: 'var(--gf-space-1, 4px) var(--gf-space-2, 8px)',
        fontSize: 'var(--gf-text-sm, 0.875rem)',
        border: '1px solid var(--gf-border-color, #E5E7EB)',
        borderRadius: 'var(--gf-border-radius-sm, 4px)',
    },
    helpIcon: {
        color: 'var(--gf-secondary-400, #6B7280)',
        cursor: 'help',
    },
};

export function SideBar({
    setShowTenementPopup,
    setTenementFilters,
    tenementFilters,
    setTenementPopup,
    layerVisibility,
    setLayerVisibility,
    deaVisibility,
    setDeaVisibility,
    deaOpacity,
    setDeaOpacity,
    deaOptions,
    setDeaOptions,
    landChange,
    setLandChange,
    terrain,
    setTerrain,
    asterVisibility,
    setAsterVisibility,
    asterOpacity,
    setAsterOpacity,
    overlayVisibility,
    setOverlayVisisbility,
    assayMarkerColourSettings,
    setAssayMarkerColourSettings,
    assayFilters,
    setAssayFilters,
    assayGeoJson,
    setAssayGeoJson,
    minedexFilters,
    setMinedexFilters,
    setMinedexPopup,
    setShowMinedexPopup,
    drillHoleFilters,
    setDrillHoleFilters,
    drillHoleColourSettings,
    setDrilHoleColourSettings,
    drillHoleGeoJson,
    prospectFilters,
    setProspectFilters,
    trigger40EWizard,
    drawCtrl,
    waypointRevision,
    presetsControl }) {

    const [tenementFiltersOpen, setTenementsFiltersOpen] = useState(false);
    const [soilsFiltersOpen, setSoilsFiltersOpen] = useState(false);
    const [minedexFiltersOpen, setMinedexFiltersOpen] = useState(false);
    const [drillholeFiltersOpen, setDrillholeFiltersOpen] = useState(false);
    const [prospectFiltersOpen, setProspectFiltersOpen] = useState(false);
    const [ocrGoldFiltersOpen, setOcrGoldFiltersOpen] = useState(false);
    const [asxAlluvialFiltersOpen, setAsxAlluvialFiltersOpen] = useState(false);
    const [waypointOptionsOpen, setWaypointOptionsOpen] = useState(false);
    // ASTER section collapsed by default — it's a large, secondary layer group.
    // Base maps open by default - it is the control people reach for most.
    const [baseMapsOpen, setBaseMapsOpen] = useState(true);
    // Gold evidence and land access are the two people check on every session -
    // "has gold been found here" and "can I legally be here" - so both open.
    const [goldEvidenceOpen, setGoldEvidenceOpen] = useState(true);
    const [landAccessOpen, setLandAccessOpen] = useState(true);
    const [adminLayersOpen, setAdminLayersOpen] = useState(true);
    const [asterOpen, setAsterOpen] = useState(false);
    const [changeOpen, setChangeOpen] = useState(false);
    const [terrainOpen, setTerrainOpen] = useState(false);

    const { goldfindermap } = useMap();
    // Gates the prospect-reports checkbox below. Hiding a control, not access
    // control — the bundle it reveals sits at a public path.
    const isAdmin = useIsAdmin();

    // Waypoint export. This was TerraDraw's own toolbar button; it is driven
    // off the control directly now that the button is gone, which also lets the
    // file be named something better than TerraDraw's `data.geojson`.
    //
    // The count is read on render and TerraDraw's store is not React state, so
    // it only refreshes when the sidebar happens to re-render. Placing a
    // waypoint does that (the mode change propagates up); DELETING one does
    // not, so the count could sit stale and the export could carry a waypoint
    // the user had removed. `waypointRevision` is bumped whenever the store
    // changes and gives the read something to hang off.
    const readWaypoints = () => drawCtrl?.getFeatures(false)?.features ?? [];
    const waypoints = useMemo(readWaypoints, [drawCtrl, waypointRevision]);

    const downloadWaypoints = () => {
        // Read again at click time rather than trusting the closure - one less
        // way for the file to disagree with what is on the map.
        const features = readWaypoints();
        if (!features.length) return;
        const blob = new Blob(
            [JSON.stringify({ type: 'FeatureCollection', features })],
            { type: 'application/geo+json' }
        );
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `goldfinder-waypoints-${new Date().toISOString().slice(0, 10)}.geojson`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Firefox and Safari have aborted downloads when the blob URL is
        // revoked in the same task as the click.
        setTimeout(() => URL.revokeObjectURL(url), 0);
    };

    const changeMapBaseLayer = (layerId) => {
        let tmp = {};
        for (let key in layerVisibility) {
            tmp[key] = layerId === key;
        }
        setLayerVisibility({ ...tmp })
    }

    // ASTER mineral overlays are additive (blend on top of base + each other),
    // so they toggle independently rather than mutually-exclusive like base maps.
    const toggleAsterLayer = (layerId) => {
        setAsterVisibility({ ...asterVisibility, [layerId]: !asterVisibility[layerId] });
    }

    // DEA change/vegetation + terrain rasters — additive, same as ASTER.
    const toggleDeaLayer = (layerId) => {
        setDeaVisibility({ ...deaVisibility, [layerId]: !deaVisibility?.[layerId] });
    }

    // Grouped by the prospecting question each layer answers, not by which
    // programme published it. The DEA `group` field still says change/terrain -
    // that describes the data source, which is not how anyone looks for gold.
    const byId = (id) => DEA_OVERLAYS.find(o => o.id === id);
    const pick = (...ids) => ids.map(byId).filter(Boolean);

    // Alteration mineralogy. ASTER and Sentinel-2 answer the same question at
    // different resolutions, so they belong together and are labelled by sensor
    // rather than sitting in two unrelated sections.
    const alterationS2 = pick('deaFerricIron', 'deaClayMica');
    // Bare rock, fresh disturbance and landform - "where do I actually look",
    // and "has someone been here before me".
    const groundLayers = pick('deaChangeMads', 'deaBareGround',
        ...(SHOW_BAREST_EARTH ? ['deaBarestEarth'] : []), 'deaMstp', 'deaWeathering');
    // Vegetation and water: context, not a gold signal.
    const vegWaterLayers = pick(...VEG_WATER_DEA_IDS);

    const setDeaOption = (patch) => setDeaOptions({ ...deaOptions, ...patch });

    const deaLayerRow = (layer) => (
        <Fragment key={layer.id}>
            <label
                title={layer.hint}
                style={{
                    ...styles.radioItem,
                    backgroundColor: deaVisibility?.[layer.id] ? 'var(--gf-primary-50, #FFF4ED)' : 'transparent',
                }}
            >
                <Form.Check
                    id={layer.id}
                    label={layer.label}
                    type="checkbox"
                    checked={!!deaVisibility?.[layer.id]}
                    onChange={() => toggleDeaLayer(layer.id)}
                    style={{ margin: 0 }}
                />
            </label>
            {/* Year + stretch only matter once the layer is actually on, so they
                stay out of the way until then. */}
            {layer.years && deaVisibility?.[layer.id] && (
                <div style={{ display: 'flex', gap: 6, padding: '0 4px 6px 26px' }}>
                    <Form.Select
                        size="sm"
                        value={deaOptions?.geomadYear ?? layer.years[layer.years.length - 1]}
                        onChange={(e) => setDeaOption({ geomadYear: e.target.value })}
                        title="Calendar year of observations summarised"
                    >
                        {[...layer.years].reverse().map((y) => (
                            <option key={y} value={y}>{y.slice(0, 4)}</option>
                        ))}
                    </Form.Select>
                    {layer.styleChoices && (
                        <Form.Select
                            size="sm"
                            value={deaOptions?.geomadStyle ?? layer.style}
                            onChange={(e) => setDeaOption({ geomadStyle: e.target.value })}
                            title="Desert-tuned pulls far more detail out of arid ground"
                        >
                            {layer.styleChoices.map((c) => (
                                <option key={c.id} value={c.id}>{c.label}</option>
                            ))}
                        </Form.Select>
                    )}
                </div>
            )}
            {/* Its own opacity, shown under the layer while it is on. It used to
                be one value shared by every DEA raster, set from a slider at the
                foot of each group - so moving it faded layers in other groups
                too, and nothing said which layer it belonged to. */}
            {deaVisibility?.[layer.id] && (
                <div style={{ paddingLeft: 22 }}>
                    <OpacitySlider
                        value={deaOptions?.opacity?.[layer.id] ?? deaOpacity ?? 0.8}
                        onChange={(v) => setDeaOption({ opacity: { ...deaOptions?.opacity, [layer.id]: v } })}
                    />
                </div>
            )}
        </Fragment>
    );

    const setPpbMoreThan = (val) => {
        setAssayFilters({ ...assayFilters, ppbmt: val.target.valueAsNumber });
    }

    const setDhGtMoreThan = (val) => {
        setDrillHoleFilters({ ...drillHoleFilters, gptmt: val.target.valueAsNumber });
    }

    // Grouped so the heading carries the product name and each option only has
    // to carry what actually differs. "100k" under GEOLOGICAL MOSAIC reads
    // unambiguously; "100k Mosaic" on its own does not.
    //
    // Regolith and Tectonic Units stay wired up in the map (and in saved
    // presets) but are deliberately not offered here - specialist layers that
    // crowd the picker without earning their place for most prospecting.
    const baseLayerGroups = [
        {
            heading: 'Imagery',
            layers: [
                { id: 'hybrid', label: 'Google' },
                // Too long for a half-width cell - span the row rather than
                // abbreviate them into something meaningless.
                // Sentinel-2 (dated, 10m) is hidden from the picker for now. Like
                // Regolith and Tectonic it stays wired up in the map and presets;
                // restore this line to offer it again:
                // { id: 'sentinel2Dated', label: 'Sentinel-2 (dated, 10m)', wide: true },
                { id: 'sentinel2', label: 'Sentinel-2 (barest earth)', wide: true },
            ],
        },
        {
            heading: 'Reference',
            layers: [
                { id: 'street', label: 'Street Maps' },
                { id: 'topo', label: 'Topographic' },
            ],
        },
        {
            heading: 'Geological Mosaic',
            layers: [
                { id: 'geo100', label: '100k' },
                { id: 'geo200', label: '250k' },
            ],
        },
        {
            // Only the 500k state layer. The 100k sheets have patchy coverage -
            // large parts of the Yilgarn come back blank - so offering it as a
            // base map mostly produced an empty screen.
            heading: 'Interpreted Bedrock',
            layers: [
                { id: 'bedrock500', label: '500k', wide: true },
            ],
        },
        {
            heading: 'Geophysics',
            layers: [
                { id: 'magnetic', label: 'Magnetic Intensity (RTP)', wide: true },
            ],
        },
    ]
        // Hidden base maps drop out here rather than being deleted, so turning
        // one back on is a single flag in gl-map-optimized.jsx.
        .filter(g => SHOW_INTERPRETED_BEDROCK || g.heading !== 'Interpreted Bedrock')
        .map(g => ({ ...g, layers: g.layers.filter(l => SHOW_BAREST_EARTH || l.id !== 'sentinel2') }))
        .filter(g => g.layers.length > 0);

    return (
        <div style={styles.sidebar}>
            {/* Header with Logo */}
            <div style={styles.header}>
                <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                    <GoldFinderLogo height={40} />
                </a>
            </div>

            {/* Content */}
            <div style={styles.content}>
                {/* Presets first - it is how people restore a whole working
                    configuration, so it belongs above the individual controls
                    it drives rather than buried between them. */}
                {presetsControl}

                {/* Base Maps Section */}
                <div style={styles.section}>
                    <div
                        style={{ ...styles.sectionHeader, cursor: 'pointer', justifyContent: 'space-between' }}
                        onClick={() => setBaseMapsOpen(!baseMapsOpen)}
                        role="button"
                        aria-expanded={baseMapsOpen}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--gf-space-2, 8px)' }}>
                            <FontAwesomeIcon icon={faMap} style={styles.sectionIcon} />
                            <h3 style={styles.sectionTitle}>Base Maps</h3>
                        </span>
                        <FontAwesomeIcon icon={baseMapsOpen ? faChevronUp : faChevronDown} size="sm" />
                    </div>
                    {baseMapsOpen && (<>
                    <div>
                        {baseLayerGroups.map(group => (
                            <div key={group.heading} style={styles.baseGroup}>
                                <div style={styles.baseGroupHeading}>{group.heading}</div>
                                <div style={styles.baseGroupGrid}>
                                    {group.layers.map(layer => (
                                        <label
                                            key={layer.id}
                                            style={{
                                                ...styles.radioItem,
                                                padding: 'var(--gf-space-1, 4px) var(--gf-space-2, 8px)',
                                                gridColumn: layer.wide ? '1 / -1' : 'auto',
                                                backgroundColor: layerVisibility[layer.id] ? 'var(--gf-primary-50, #FFF4ED)' : 'transparent',
                                            }}
                                        >
                                            <Form.Check
                                                id={layer.id}
                                                name='baseLayerChange'
                                                label={layer.label}
                                                type="radio"
                                                checked={layerVisibility[layer.id]}
                                                onChange={() => changeMapBaseLayer(layer.id)}
                                                style={{ margin: 0 }}
                                            />
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    {SHOW_BEDROCK_LEGEND && layerVisibility.bedrock500 && (
                        <div style={{ padding: '8px 4px 4px', borderTop: '1px solid var(--gf-border-color, #E5E7EB)' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em',
                                          textTransform: 'uppercase', color: 'var(--gf-secondary-500, #4B5563)',
                                          marginBottom: 6 }}>
                                500k Bedrock — legend
                            </div>
                            {/* Hundreds of mapped units, so the key is 4096px tall.
                                Give it its own scroll box rather than squashing it
                                into something unreadable. */}
                            <div style={{ maxHeight: 260, overflowY: 'auto', overflowX: 'auto',
                                          border: '1px solid var(--gf-border-color, #E5E7EB)',
                                          borderRadius: 6, background: '#fff' }}>
                                <img
                                    src={slipGeologyLegendUrl(16)}
                                    alt="1:500 000 interpreted bedrock geology legend"
                                    loading="lazy"
                                    style={{ display: 'block', width: '100%', height: 'auto' }}
                                />
                            </div>
                        </div>
                    )}
                    </>)}
                </div>

                {/* ASTER Mineral Maps Section — additive overlays (checkbox + opacity).
                    Collapsed by default (large, secondary layer group). */}
                <div style={styles.section}>
                    <div
                        style={{ ...styles.sectionHeader, cursor: 'pointer', justifyContent: 'space-between' }}
                        onClick={() => setGoldEvidenceOpen(!goldEvidenceOpen)}
                        role="button"
                        aria-expanded={goldEvidenceOpen}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--gf-space-2, 8px)' }}>
                            <FontAwesomeIcon icon={faLayerGroup} style={styles.sectionIcon} />
                            <h3 style={styles.sectionTitle}>Gold Evidence</h3>
                        </span>
                        <FontAwesomeIcon icon={goldEvidenceOpen ? faChevronUp : faChevronDown} size="sm" />
                    </div>
                    {goldEvidenceOpen && (<>

                    {/* Tenements Overlay */}
                    

                    {/* Soils Overlay */}
                    <div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='soils-overlay'
                                    label='Soils'
                                    checked={overlayVisibility.soils}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, soils: !overlayVisibility.soils })}
                                />
                            </div>
                            <div style={styles.overlayControls}>
                                <div style={styles.inputGroup}>
                                    <span style={styles.inputLabel}>PPB &gt;</span>
                                    <FontAwesomeIcon
                                        icon={faQuestionCircle}
                                        size="xs"
                                        style={styles.helpIcon}
                                        title="Gold Parts Per Billion"
                                    />
                                    <Form.Control
                                        size="sm"
                                        style={styles.numberInput}
                                        defaultValue={assayFilters.ppbmt}
                                        onChange={setPpbMoreThan}
                                        type='number'
                                        step="1"
                                    />
                                </div>
                                <button
                                    style={{
                                        ...styles.filterButton,
                                        ...(soilsFiltersOpen ? styles.filterButtonActive : {}),
                                    }}
                                    onClick={() => setSoilsFiltersOpen(!soilsFiltersOpen)}
                                    aria-expanded={soilsFiltersOpen}
                                    title="Filter options"
                                >
                                    <FontAwesomeIcon icon={soilsFiltersOpen ? faChevronUp : faFilter} size="sm" />
                                </button>
                            </div>
                        </div>
                        <Collapse in={soilsFiltersOpen}>
                            <div style={styles.filterContent}>
                                <AssayQueryToolMapLibre
                                    assayGeoJson={assayGeoJson}
                                    assayMarkerColourSettings={assayMarkerColourSettings}
                                    setAssayMarkerColourSettings={setAssayMarkerColourSettings}
                                    assayFilters={assayFilters}
                                    setAssayFilters={setAssayFilters}
                                    goldfindermap={goldfindermap}
                                    setAssayGeoJson={setAssayGeoJson}
                                />
                            </div>
                        </Collapse>
                    </div>

                    {/* Drill Hole Overlay */}
                    <div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='dh-overlay'
                                    label='Drill Hole'
                                    checked={overlayVisibility.dh}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, dh: !overlayVisibility.dh })}
                                />
                            </div>
                            <div style={styles.overlayControls}>
                                <div style={styles.inputGroup}>
                                    <span style={styles.inputLabel}>G/T &gt;</span>
                                    <FontAwesomeIcon
                                        icon={faQuestionCircle}
                                        size="xs"
                                        style={styles.helpIcon}
                                        title="Gold grams per tonne"
                                    />
                                    <Form.Control
                                        size="sm"
                                        style={styles.numberInput}
                                        defaultValue={drillHoleFilters.gptmt}
                                        onChange={setDhGtMoreThan}
                                        type='number'
                                        step="0.1"
                                    />
                                </div>
                                <button
                                    style={{
                                        ...styles.filterButton,
                                        ...(drillholeFiltersOpen ? styles.filterButtonActive : {}),
                                    }}
                                    onClick={() => setDrillholeFiltersOpen(!drillholeFiltersOpen)}
                                    aria-expanded={drillholeFiltersOpen}
                                    title="Filter options"
                                >
                                    <FontAwesomeIcon icon={drillholeFiltersOpen ? faChevronUp : faFilter} size="sm" />
                                </button>
                            </div>
                        </div>
                        <Collapse in={drillholeFiltersOpen}>
                            <div style={styles.filterContent}>
                                <DrillHoleQueryTool
                                    drillHoleFilters={drillHoleFilters}
                                    setDrillHoleFilters={setDrillHoleFilters}
                                    drillHoleColourSettings={drillHoleColourSettings}
                                    setDrilHoleColourSettings={setDrilHoleColourSettings}
                                    drillHoleGeoJson={drillHoleGeoJson}
                                />
                            </div>
                        </Collapse>
                    </div>

                    {/* MINEDEX Overlay */}
                    <div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='minedex-overlay'
                                    label='MINEDEX Data'
                                    checked={overlayVisibility.minedex}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, minedex: !overlayVisibility.minedex })}
                                />
                            </div>
                            <button
                                style={{
                                    ...styles.filterButton,
                                    ...(minedexFiltersOpen ? styles.filterButtonActive : {}),
                                }}
                                onClick={() => setMinedexFiltersOpen(!minedexFiltersOpen)}
                                aria-expanded={minedexFiltersOpen}
                                title="Filter options"
                            >
                                <FontAwesomeIcon icon={minedexFiltersOpen ? faChevronUp : faFilter} size="sm" />
                            </button>
                        </div>
                        <Collapse in={minedexFiltersOpen}>
                            <div style={styles.filterContent}>
                                <MinedexQueryTool
                                    minedexFilters={minedexFilters}
                                    setMinedexFilters={setMinedexFilters}
                                    goldfindermap={goldfindermap}
                                    setMinedexPopup={setMinedexPopup}
                                    setShowMinedexPopup={setShowMinedexPopup}
                                />
                            </div>
                        </Collapse>
                    </div>

                    {/* Simple Overlays (no filters) */}

                    

                    

                    
                    </>)}
                </div>
                {/* Admin Layers - internal working material: prospect reports,
                    OCR gold values and the georeferenced plates. The whole
                    section is absent for anyone without the admin claim, and
                    each overlay checks the claim again before it draws. */}
                {isAdmin && (
                <div style={styles.section}>
                    <div
                        style={{ ...styles.sectionHeader, cursor: 'pointer', justifyContent: 'space-between' }}
                        onClick={() => setAdminLayersOpen(!adminLayersOpen)}
                        role="button"
                        aria-expanded={adminLayersOpen}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--gf-space-2, 8px)' }}>
                            <FontAwesomeIcon icon={faUserShield} style={styles.sectionIcon} />
                            <h3 style={styles.sectionTitle}>Admin Layers</h3>
                        </span>
                        <FontAwesomeIcon icon={adminLayersOpen ? faChevronUp : faChevronDown} size="sm" />
                    </div>
                    {adminLayersOpen && (<>
                    {/* OCR gold values from the local KB GeoServer. Offered only
                        when that endpoint is configured - a dev server, or
                        REACT_APP_OCR_GOLD_WMS - never in a production build. */}
                    {OCR_GOLD_AVAILABLE && (
                    <div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='ocr-gold-overlay'
                                    label={OCR_GOLD_LOCAL ? 'OCR gold values (local)' : 'OCR gold values'}
                                    checked={!!overlayVisibility.ocrGold}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, ocrGold: !overlayVisibility.ocrGold })}
                                />
                            </div>
                            {/* Which of the table reader's confidence levels are drawn. */}
                            <button
                                style={{
                                    ...styles.filterButton,
                                    ...(ocrGoldFiltersOpen ? styles.filterButtonActive : {}),
                                }}
                                onClick={() => setOcrGoldFiltersOpen(!ocrGoldFiltersOpen)}
                                aria-expanded={ocrGoldFiltersOpen}
                                title="Filter options"
                            >
                                <FontAwesomeIcon icon={ocrGoldFiltersOpen ? faChevronUp : faFilter} size="sm" />
                            </button>
                        </div>
                        <Collapse in={ocrGoldFiltersOpen}>
                            <div style={styles.filterContent}>
                                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Read confidence</div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    {OCR_GOLD_CONFIDENCE.map((c) => (
                                        <Form.Check
                                            key={c.key}
                                            id={`ocr-gold-conf-${c.value}`}
                                            label={c.label}
                                            type="checkbox"
                                            // Absent means shown: presets saved before this control.
                                            checked={overlayVisibility[c.key] !== false}
                                            onChange={() => setOverlayVisisbility({
                                                ...overlayVisibility,
                                                [c.key]: overlayVisibility[c.key] === false,
                                            })}
                                        />
                                    ))}
                                </div>
                            </div>
                        </Collapse>
                        {overlayVisibility.ocrGold && (
                            <div style={{ padding: '4px 8px 8px', fontSize: 11, color: '#6b6b6b' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginBottom: 4 }}>
                                    {OCR_GOLD_LEGEND.map((b) => (
                                        <span key={b.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            <span style={{ width: 9, height: 9, borderRadius: '50%', background: b.colour, border: '1.4px solid #000' }} />
                                            {b.label}
                                        </span>
                                    ))}
                                </div>
                                Black edge: read from a report page, not an assay table.
                                {' '}Points from zoom {OCR_GOLD_VECTOR_MINZOOM}; toggle off/on to reload.
                            </div>
                        )}
                    </div>
                    )}

                    {/* ASX nugget & alluvial signals (goldfinder-llm-kb docs/23).
                        Company-copyright derived data: admin only, offered when
                        REACT_APP_ALLUVIAL_SIGNAL_WMS (or a dev server) is set. */}
                    {ALLUVIAL_SIGNAL_AVAILABLE && (
                    <div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='asx-alluvial-overlay'
                                    label={ALLUVIAL_SIGNAL_LOCAL ? 'ASX nugget & alluvial signals (local)' : 'ASX nugget & alluvial signals'}
                                    checked={!!overlayVisibility.asxAlluvial}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, asxAlluvial: !overlayVisibility.asxAlluvial })}
                                />
                            </div>
                            <button
                                style={{
                                    ...styles.filterButton,
                                    ...(asxAlluvialFiltersOpen ? styles.filterButtonActive : {}),
                                }}
                                onClick={() => setAsxAlluvialFiltersOpen(!asxAlluvialFiltersOpen)}
                                aria-expanded={asxAlluvialFiltersOpen}
                                title="Filter options"
                            >
                                <FontAwesomeIcon icon={asxAlluvialFiltersOpen ? faChevronUp : faFilter} size="sm" />
                            </button>
                        </div>
                        <Collapse in={asxAlluvialFiltersOpen}>
                            <div style={styles.filterContent}>
                                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Signal</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
                                    {ALLUVIAL_SIGNAL_CLASSES.map((c) => (
                                        <Form.Check
                                            key={c.key}
                                            id={`asx-alluvial-class-${c.value}`}
                                            label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ width: 9, height: 9, borderRadius: '50%', background: c.colour }} />{c.label}
                                            </span>}
                                            type="checkbox"
                                            // Absent means shown: presets saved before this control.
                                            checked={overlayVisibility[c.key] !== false}
                                            onChange={() => setOverlayVisisbility({
                                                ...overlayVisibility,
                                                [c.key]: overlayVisibility[c.key] === false,
                                            })}
                                        />
                                    ))}
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 600, margin: '8px 0 4px' }} title="First release, or the latest release reporting new gold at the site; recaps do not count">New gold reported within</div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    {ALLUVIAL_RECENCY.map((r) => (
                                        <Form.Check
                                            key={r.value}
                                            id={`asx-alluvial-recency-${r.value}`}
                                            name="asx-alluvial-recency"
                                            label={r.label}
                                            type="radio"
                                            checked={alluvialRecency(overlayVisibility) === r.value}
                                            onChange={() => setOverlayVisisbility({ ...overlayVisibility, ...alluvialRecencyPatch(r.value) })}
                                        />
                                    ))}
                                </div>
                            </div>
                        </Collapse>
                        {overlayVisibility.asxAlluvial && (
                            <div style={{ padding: '4px 8px 8px', fontSize: 11, color: '#6b6b6b' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginBottom: 4 }}>
                                    {ALLUVIAL_RECENCY_BANDS.map((b) => (
                                        <span key={b.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            <span style={{
                                                width: Math.round(9 * b.scale), height: Math.round(9 * b.scale), borderRadius: '50%',
                                                background: '#d9480f', opacity: b.opacity,
                                                border: `1.3px solid ${b.stroke === '#ffffff' ? '#ced4da' : b.stroke}`,
                                            }} />
                                            {b.label}
                                        </span>
                                    ))}
                                </div>
                                Size by how recently new gold was reported; colour by signal. Rings from zoom {ALLUVIAL_SIGNAL_ERROR_MINZOOM} show the location error.
                                {' '}Click a point for the source announcement.
                            </div>
                        )}
                    </div>
                    )}

                    {/* Georeferenced map plates (ASX, A-number) from the local
                        GeoServer. Renders nothing without that endpoint, so
                        never in a production build. */}
                    <PlatesSidebarCards
                        overlayVisibility={overlayVisibility}
                        setOverlayVisisbility={setOverlayVisisbility}
                        styles={styles} />

                    {/* Prospect reports. */}
                    <div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='prospect-overlay'
                                    label='Prospect reports'
                                    checked={!!overlayVisibility.prospect}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, prospect: !overlayVisibility.prospect })}
                                />
                            </div>
                            {/* Soils / drill holes / findings. The overlay draws
                                all three at once and they overlap heavily on a
                                dense recovered grid. */}
                            <button
                                style={{
                                    ...styles.filterButton,
                                    ...(prospectFiltersOpen ? styles.filterButtonActive : {}),
                                }}
                                onClick={() => setProspectFiltersOpen(!prospectFiltersOpen)}
                                aria-expanded={prospectFiltersOpen}
                                title="Filter options"
                            >
                                <FontAwesomeIcon icon={prospectFiltersOpen ? faChevronUp : faFilter} size="sm" />
                            </button>
                        </div>
                        <Collapse in={prospectFiltersOpen}>
                            <div style={styles.filterContent}>
                                <ProspectFilter
                                    prospectFilters={prospectFilters}
                                    setProspectFilters={setProspectFilters}
                                />
                            </div>
                        </Collapse>
                    </div>
                    </>)}
                </div>
                )}
                {/* Land access - administrative, not geological. */}
                <div style={styles.section}>
                    <div
                        style={{ ...styles.sectionHeader, cursor: 'pointer', justifyContent: 'space-between' }}
                        onClick={() => setLandAccessOpen(!landAccessOpen)}
                        role="button"
                        aria-expanded={landAccessOpen}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--gf-space-2, 8px)' }}>
                            <FontAwesomeIcon icon={faMap} style={styles.sectionIcon} />
                            <h3 style={styles.sectionTitle}>Land Access</h3>
                        </span>
                        <FontAwesomeIcon icon={landAccessOpen ? faChevronUp : faChevronDown} size="sm" />
                    </div>
                    {landAccessOpen && (<>
<div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='tenements_overlay'
                                    label='Tenements'
                                    checked={overlayVisibility.tenements}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, tenements: !overlayVisibility.tenements })}
                                />
                            </div>
                            <button
                                style={{
                                    ...styles.filterButton,
                                    ...(tenementFiltersOpen ? styles.filterButtonActive : {}),
                                }}
                                onClick={() => setTenementsFiltersOpen(!tenementFiltersOpen)}
                                aria-expanded={tenementFiltersOpen}
                                title="Filter options"
                            >
                                <FontAwesomeIcon icon={tenementFiltersOpen ? faChevronUp : faFilter} size="sm" />
                            </button>
                        </div>
                        <Collapse in={tenementFiltersOpen}>
                            <div style={styles.filterContent}>
                                <TenementQueryTool
                                    setTenementFilters={setTenementFilters}
                                    tenementFilters={tenementFilters}
                                    setTenementPopup={setTenementPopup}
                                    goldfindermap={goldfindermap}
                                    setShowTenementPopup={setShowTenementPopup}
                                    overlayVisibility={overlayVisibility}
                                    setOverlayVisisbility={setOverlayVisisbility}
                                    trigger40EWizard={trigger40EWizard}
                                />
                            </div>
                        </Collapse>
                    </div>
<div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='graticles-overlay'
                                    label='1 Min Graticules'
                                    checked={overlayVisibility.graticles}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, graticles: !overlayVisibility.graticles })}
                                />
                            </div>
                        </div>
                    </div>
                    </>)}
                </div>
                <div style={styles.section}>
                    <div
                        style={{ ...styles.sectionHeader, cursor: 'pointer', justifyContent: 'space-between' }}
                        onClick={() => setAsterOpen(!asterOpen)}
                        role="button"
                        aria-expanded={asterOpen}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--gf-space-2, 8px)' }}>
                            <FontAwesomeIcon icon={faGem} style={styles.sectionIcon} />
                            <h3 style={styles.sectionTitle}>Structure &amp; Alteration</h3>
                        </span>
                        <FontAwesomeIcon icon={asterOpen ? faChevronUp : faChevronDown} size="sm" />
                    </div>
                    {asterOpen && (<>
                    <div style={styles.baseGroupHeading}>Alteration — ASTER 30m</div>
                    <div style={styles.radioGroup}>
                        {ASTER_LAYERS.map(layer => (
                            <label
                                key={layer.id}
                                style={{
                                    ...styles.radioItem,
                                    backgroundColor: asterVisibility?.[layer.id] ? 'var(--gf-primary-50, #FFF4ED)' : 'transparent',
                                }}
                            >
                                <Form.Check
                                    id={layer.id}
                                    label={layer.label}
                                    type="checkbox"
                                    checked={!!asterVisibility?.[layer.id]}
                                    onChange={() => toggleAsterLayer(layer.id)}
                                    style={{ margin: 0 }}
                                />
                            </label>
                        ))}
                        {ASTER_LAYERS.some((l) => asterVisibility?.[l.id]) && (
                            <OpacitySlider
                                value={asterOpacity ?? 0.75}
                                onChange={setAsterOpacity}
                            />
                        )}

                        {/* Legends — one per enabled ASTER layer, pulled from the
                            service's GetLegendGraphic so the key always matches the map. */}
                        {ASTER_LAYERS.filter(l => asterVisibility?.[l.id]).map(l => (
                            <div key={`legend-${l.id}`} style={{ padding: '6px 4px', borderTop: '1px solid #eee' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: 4 }}>{l.label}</div>
                                <img
                                    src={asterLegendUrl(l.wms)}
                                    alt={`${l.label} legend`}
                                    loading="lazy"
                                    style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                                />
                            </div>
                        ))}
                    </div>

                    <div style={styles.baseGroupHeading}>Alteration — Sentinel-2 10m</div>
                    <div style={styles.radioGroup}>
                        {alterationS2.map(deaLayerRow)}
                    </div>

                    <div style={styles.baseGroupHeading}>Structure</div>
                    <div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='wa-faults-overlay'
                                    label='Faults & Shear Zones'
                                    checked={overlayVisibility.waFaults}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, waFaults: !overlayVisibility.waFaults })}
                                />
                            </div>
                        </div>
                    </div>
                    </>)}
                </div>

                {/* Change & vegetation — DEA rasters (M4). Collapsed by default;
                    these answer "what changed here", not "where am I". */}
                <div style={styles.section}>
                    <div
                        style={{ ...styles.sectionHeader, cursor: 'pointer', justifyContent: 'space-between' }}
                        onClick={() => setChangeOpen(!changeOpen)}
                        role="button"
                        aria-expanded={changeOpen}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--gf-space-2, 8px)' }}>
                            <FontAwesomeIcon icon={faSeedling} style={styles.sectionIcon} />
                            <h3 style={styles.sectionTitle}>Ground &amp; Disturbance</h3>
                        </span>
                        <FontAwesomeIcon icon={changeOpen ? faChevronUp : faChevronDown} size="sm" />
                    </div>
                    {changeOpen && (
                        <div style={styles.radioGroup}>
                            <label
                                title="Shaded relief over whatever base map is showing"
                                style={{
                                    ...styles.radioItem,
                                    backgroundColor: terrain?.hillshade ? 'var(--gf-primary-50, #FFF4ED)' : 'transparent',
                                }}
                            >
                                <Form.Check
                                    id="terrain-hillshade-toggle"
                                    label="Hillshade"
                                    type="checkbox"
                                    checked={!!terrain?.hillshade}
                                    onChange={() => setTerrain({ ...terrain, hillshade: !terrain?.hillshade })}
                                    style={{ margin: 0 }}
                                />
                            </label>
                            {groundLayers.map(deaLayerRow)}

                            {/* Detected changes: hidden for now (SHOW_LAND_CHANGE). */}
                            {SHOW_LAND_CHANGE && (<>
                            {/* Our own derived layer. Sits with the DEA change
                                products because it answers the same question —
                                what changed here — but it has its own service,
                                its own legend and a clickable popup. */}
                            <div style={{ borderTop: '1px solid #eee', marginTop: 6, paddingTop: 6 }} />
                            <label
                                title="Ground that lost vegetation and kept it off. Click a polygon for its details."
                                style={{
                                    ...styles.radioItem,
                                    backgroundColor: landChange?.visible ? 'var(--gf-primary-50, #FFF4ED)' : 'transparent',
                                }}
                            >
                                <Form.Check
                                    id="landChangeVisible"
                                    label="Detected changes 1991-2023"
                                    type="checkbox"
                                    checked={!!landChange?.visible}
                                    onChange={() => setLandChange({ ...landChange, visible: !landChange?.visible })}
                                    style={{ margin: 0 }}
                                />
                            </label>

                            {landChange?.visible && (
                                <>
                                    <OpacitySlider
                                        min={0.1}
                                        value={landChange?.opacity ?? 0.85}
                                        onChange={(v) => setLandChange({ ...landChange, opacity: v })}
                                    />
                                </>
                            )}
                            </>)}

                        </div>
                    )}
                </div>

                {/* Vegetation & Water: hidden for now (SHOW_VEG_WATER). */}
                {SHOW_VEG_WATER && (<>
                {/* Terrain (M5) — shaded relief from the elevation tiles, plus
                    the two interpretive DEA terrain rasters. */}
                <div style={styles.section}>
                    <div
                        style={{ ...styles.sectionHeader, cursor: 'pointer', justifyContent: 'space-between' }}
                        onClick={() => setTerrainOpen(!terrainOpen)}
                        role="button"
                        aria-expanded={terrainOpen}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--gf-space-2, 8px)' }}>
                            <FontAwesomeIcon icon={faMountain} style={styles.sectionIcon} />
                            <h3 style={styles.sectionTitle}>Vegetation &amp; Water</h3>
                        </span>
                        <FontAwesomeIcon icon={terrainOpen ? faChevronUp : faChevronDown} size="sm" />
                    </div>
                    {terrainOpen && (
                        <div style={styles.radioGroup}>
                            {vegWaterLayers.map(deaLayerRow)}
                        </div>
                    )}
                </div>
                </>)}

                {/* Overlays Section */}




                {/* Your own record. */}
                <div style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <FontAwesomeIcon icon={faLayerGroup} style={styles.sectionIcon} />
                        <h3 style={styles.sectionTitle}>My Mapping</h3>
                    </div>
<div style={styles.overlayCard}>
                        <div style={styles.overlayHeader}>
                            <div style={styles.overlayCheckbox}>
                                <Form.Check
                                    id='markers-overlay'
                                    label='Waypoints'
                                    checked={overlayVisibility.waypoints}
                                    onChange={() => setOverlayVisisbility({ ...overlayVisibility, waypoints: !overlayVisibility.waypoints })}
                                />
                            </div>
                            <button
                                style={{
                                    ...styles.filterButton,
                                    ...(waypointOptionsOpen ? styles.filterButtonActive : {}),
                                }}
                                onClick={() => setWaypointOptionsOpen(!waypointOptionsOpen)}
                                aria-expanded={waypointOptionsOpen}
                                title="Waypoint options"
                            >
                                <FontAwesomeIcon icon={waypointOptionsOpen ? faChevronUp : faChevronDown} size="sm" />
                            </button>
                        </div>
                        <Collapse in={waypointOptionsOpen}>
                            <div style={styles.filterContent}>
                                <FilterSection label="Export" meta={waypoints.length ? `${waypoints.length} saved` : 'None yet'} first>
                                    {waypoints.length > 0 && (
                                        <ChipRow>
                                            <Chip
                                                onClick={downloadWaypoints}
                                                title="Save every waypoint to a GeoJSON file"
                                            >
                                                Download GeoJSON
                                            </Chip>
                                        </ChipRow>
                                    )}
                                </FilterSection>
                            </div>
                        </Collapse>
                    </div>
                </div>

            </div>
        </div>
    );
}
